import React, { useContext, useEffect, useMemo, useState } from 'react'
import API from '../../../services/API'
import {
  Box,
  Chip,
  LinearProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Paper,
} from '@mui/material'
import { UserContext } from '../../../App'
import { useNavigate } from 'react-router-dom'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import DescriptionIcon from '@mui/icons-material/Description'
import moment from 'moment'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import EmptyState from '../../../components/EmptyState/EmptyState'

const PolicyLibrary = () => {
  const [loading, setLoading] = useState(true)
  const [policyList, setPolicyList] = useState([])
  const [totalActiveStaff, setTotalActiveStaff] = useState(0)

  const { user } = useContext(UserContext)
  const navigate = useNavigate()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const allowedTeams = ['Manager', 'Governance', 'Personnel', 'Supervisor']

  const isGovernanceUser = user.employee?.teams?.some((team) =>
    allowedTeams.includes(team),
  )

  // Matches the site-configuration Governance group gate — only these users
  // can actually reach /site-configuration/policies.
  const canConfigurePolicies =
    user?.security?.isSiteAdmin || user.employee?.teams?.includes('Governance')

  const getAcknowledgementForUser = (acknowledgedBy = [], userId) => {
    return acknowledgedBy.find((ack) => {
      const ackUserId = ack?.user?._id || ack?.user
      return ackUserId?.toString() === userId?.toString()
    })
  }

  useEffect(() => {
    API.get('/policies/library')
      .then((res) => {
        const rows = res.data.policies.map((policy) => {
          const latestPolicy = policy.policies?.[0]
          const acknowledgements = latestPolicy?.acknowledgedBy || []

          const currentUserAcknowledgement = getAcknowledgementForUser(
            acknowledgements,
            user._id,
          )

          return {
            id: policy._id,
            category: policy.category,
            policy: policy.policyTitle,
            date: latestPolicy?.date,
            acknowledgedCount: acknowledgements.length,
            acknowledgedRate:
              res.data.totalActiveStaff > 0
                ? acknowledgements.length / res.data.totalActiveStaff
                : 0,
            acknowledgedByCurrentUser: Boolean(currentUserAcknowledgement),
            currentUserAcknowledgedAt:
              currentUserAcknowledgement?.acknowledgedAt,
            hasExistingPolicy: policy.policies.length > 0,
          }
        })

        setPolicyList(rows)
        setTotalActiveStaff(res.data.totalActiveStaff)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }, [user._id])

  const columns = useMemo(
    () => [
      {
        field: 'readByMe',
        headerName: '',
        width: 90,
        sortable: false,
        renderCell: (params) => {
          if (!params.row.hasExistingPolicy) return null

          return params.row.acknowledgedByCurrentUser ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Read"
              size="small"
              color="success"
              variant="filled"
              sx={{ fontWeight: 700 }}
            />
          ) : (
            <Chip
              icon={<PriorityHighIcon />}
              label="Due"
              size="small"
              color="error"
              variant="filled"
              sx={{ fontWeight: 700 }}
            />
          )
        },
      },
      {
        field: 'policy',
        headerName: 'Policy',
        flex: 1.2,
        minWidth: 260,
        renderCell: (params) => (
          <Stack spacing={0.25}>
            <Typography fontWeight={700} color="text.primary">
              {params.row.policy}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {params.row.category}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'date',
        headerName: 'Published',
        flex: 0.35,
        minWidth: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) =>
          params.row.date ? moment(params.row.date).format('DD MMM YYYY') : '-',
      },
      {
        field: 'currentUserAcknowledgedAt',
        headerName: 'My acknowledgement',
        flex: 0.5,
        minWidth: 190,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              width="100%"
            >
              {!params.row.hasExistingPolicy ? (
                <Typography variant="body2" color="text.secondary">
                  Not uploaded
                </Typography>
              ) : !params.row.acknowledgedByCurrentUser ? (
                <Typography variant="body2" color="error.main" fontWeight={700}>
                  Outstanding
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="success.main"
                  fontWeight={700}
                >
                  {moment(params.row.currentUserAcknowledgedAt).format(
                    'DD/MM/YYYY HH:mm',
                  )}
                </Typography>
              )}
            </Box>
          )
        },
      },
      {
        field: 'acknowledgedRate',
        headerName: 'Compliance',
        flex: 0.6,
        minWidth: 220,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const percent = Math.round(params.row.acknowledgedRate * 100)

          return (
            <Stack spacing={0.75} width="100%">
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  {params.row.acknowledgedCount}/{totalActiveStaff} staff
                </Typography>

                <Typography variant="caption" fontWeight={800}>
                  {percent}%
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{
                  height: 8,
                  borderRadius: 99,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 99,
                  },
                }}
                color={
                  percent >= 90
                    ? 'success'
                    : percent >= 60
                      ? 'warning'
                      : 'error'
                }
              />
            </Stack>
          )
        },
      },
    ],
    [totalActiveStaff],
  )

  const stats = useMemo(() => {
    const uploaded = policyList.filter((p) => p.hasExistingPolicy).length
    const acknowledged = policyList.filter(
      (p) => p.acknowledgedByCurrentUser,
    ).length
    const outstanding = uploaded - acknowledged

    return { uploaded, acknowledged, outstanding }
  }, [policyList])

  return (
    <Box
      sx={
        {
          // minHeight: '100%',
          // p: { xs: 1.5, md: 3 },
          // background:
          // 'linear-gradient(135deg, rgba(15,23,42,0.04), rgba(14,165,233,0.06))',
        }
      }
    >
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 4,
            color: 'white',
            background:
              'linear-gradient(135deg, #0B1F3A 0%, #146C94 55%, #22A6B3 100%)',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <DescriptionIcon sx={{ fontSize: 34 }} />

                <Box>
                  <Typography variant="h4" fontWeight={900} color="white">
                    Policy Library
                  </Typography>

                  <Typography sx={{ opacity: 0.85, color: 'white' }}>
                    Review, acknowledge and monitor current governance
                    documents.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={`${stats.uploaded} uploaded`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.16)',
                  color: 'white',
                  fontWeight: 700,
                }}
              />

              <Chip
                label={`${stats.acknowledged} acknowledged`}
                sx={{
                  bgcolor: 'rgba(46,204,113,0.22)',
                  color: 'white',
                  fontWeight: 700,
                }}
              />

              <Chip
                label={`${stats.outstanding} outstanding`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.16)',
                  color: 'white',
                  fontWeight: 700,
                }}
              />
            </Stack>
          </Stack>
        </Paper>

        {!loading && policyList.length === 0 ? (
          <EmptyState
            icon={DescriptionIcon}
            title="No policies in your library yet"
            description={
              canConfigurePolicies
                ? 'Policies uploaded here are shown to every member of staff to read and acknowledge, and acknowledgement compliance is tracked automatically.'
                : "Your organisation hasn't published any policies yet. Once your governance team uploads them, they'll appear here for you to read and acknowledge."
            }
            steps={
              canConfigurePolicies
                ? [
                    'Open Site Configuration → Governance → Policies and add each policy title with its category.',
                    'Each policy then appears here — open it and upload the current PDF version.',
                    'Staff are prompted to read and acknowledge it, and the compliance bar tracks who has.',
                  ]
                : undefined
            }
            actions={
              canConfigurePolicies
                ? [
                    {
                      label: 'Set Up Policies',
                      to: '/site-configuration/policies',
                    },
                  ]
                : []
            }
            docsHref="/docs/clinical-governance/policies-and-sops"
          />
        ) : (
        <Paper
          elevation={0}
          sx={{
            height: 650,
            p: 2,
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
          }}
        >
          <CustomDataGrid
            rows={policyList}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            onRowClick={(params) => navigate(params.id)}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  date: !isMobile,
                  currentUserAcknowledgedAt: !isMobile,
                  acknowledgedRate: !isMobile && isGovernanceUser,
                },
              },
              sorting: {
                sortModel: [{ field: 'policy', sort: 'asc' }],
              },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                borderRadius: 3,
                bgcolor: 'rgba(11,31,58,0.04)',
                borderBottom: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 800,
              },
              '& .MuiDataGrid-row': {
                borderRadius: 3,
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: 'rgba(20,108,148,0.06)',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
            }}
          />
        </Paper>
        )}
      </Stack>
    </Box>
  )
}

export default PolicyLibrary
